import {
  type Dispatch,
  type MutableRefObject,
  type MouseEvent as ReactMouseEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import { computeGuidePositionAtClient } from '../helpers/guide-position'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { DraggingGuide } from '../types/dragging-guide'

/**
 * Creating a guide by dragging out of a ruler: mouse-down on a ruler starts a preview guide that
 * follows the pointer, and release adds it (selected) through `updateGuides`.
 */
export function useRulerGuideCreate({
  scrollRef,
  getCanvasOrigin,
  scale,
  showGuides,
  onShowGuidesRestore,
  updateGuides,
  setSelectedGuideIds,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  getCanvasOrigin: () => { originX: number; originY: number }
  scale: number
  showGuides: boolean
  onShowGuidesRestore: CenterCanvasConfig['onShowGuidesRestore']
  updateGuides: (next: Guide[] | ((prev: Guide[]) => Guide[])) => void
  setSelectedGuideIds: Dispatch<SetStateAction<Set<string>>>
}) {
  // Ruler-drag state: creating a new guide by dragging from a ruler
  const [draggingGuide, setDraggingGuide] = useState<DraggingGuide | null>(null)

  // ── Ruler drag handlers (creating new guides) ──
  const handleRulerMouseDown = (e: ReactMouseEvent, type: 'horizontal' | 'vertical') => {
    if (!showGuides) {
      onShowGuidesRestore?.()
    }

    e.preventDefault()
    const scroller = scrollRef.current

    if (!scroller) return

    const origin = getCanvasOrigin()
    const position = computeGuidePositionAtClient(
      scroller,
      origin,
      type,
      e.clientX,
      e.clientY,
      scale
    )

    setDraggingGuide({ type, position })

    setSelectedGuideIds(new Set())
  }

  const handleRulerDragMove = useCallback(
    (e: MouseEvent) => {
      if (!draggingGuide) return
      const scroller = scrollRef.current

      if (!scroller) return

      const origin = getCanvasOrigin()
      const position = computeGuidePositionAtClient(
        scroller,
        origin,
        draggingGuide.type,
        e.clientX,
        e.clientY,
        scale
      )

      setDraggingGuide({ ...draggingGuide, position })
    },
    [draggingGuide, getCanvasOrigin, scale, scrollRef]
  )

  const handleRulerDragEnd = useCallback(() => {
    if (!draggingGuide) return
    const newGuide: Guide = {
      id: `guide-${Date.now()}`,
      type: draggingGuide.type,
      position: Math.round(draggingGuide.position * 1000) / 1000,
    }

    updateGuides((prev) => [...prev, newGuide])
    setSelectedGuideIds(new Set([newGuide.id]))
    setDraggingGuide(null)
  }, [draggingGuide, updateGuides, setSelectedGuideIds])

  // Global mouse listeners for ruler drag (new guide creation)
  useEffect(() => {
    if (!draggingGuide) return

    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', handleRulerDragMove)
    window.addEventListener('mouseup', handleRulerDragEnd)
    return () => {
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', handleRulerDragMove)
      window.removeEventListener('mouseup', handleRulerDragEnd)
    }
  }, [draggingGuide, handleRulerDragMove, handleRulerDragEnd])

  return { draggingGuide, handleRulerMouseDown }
}
